import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Input } from './input';
import { Label } from './label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { usStates } from './state-input';
import { MapPin, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

// Global tracking to prevent multiple script loads
let googleMapsInitPromise: Promise<void> | null = null;

/**
 * Wait for Google Maps Places API to be fully available.
 * With loading=async, the places library loads asynchronously after the main script.
 * This function polls until window.google.maps.places is available.
 */
const waitForPlacesAPI = (maxWaitMs = 10000, intervalMs = 100): Promise<void> => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    
    const check = () => {
      if (window.google?.maps?.places?.AutocompleteService) {
        resolve();
        return;
      }
      
      if (Date.now() - startTime > maxWaitMs) {
        reject(new Error('Timeout waiting for Google Maps Places API'));
        return;
      }
      
      setTimeout(check, intervalMs);
    };
    
    check();
  });
};

// Load Google Maps script only once globally
const loadGoogleMapsScript = (): Promise<void> => {
  // Return existing promise if already loading/loaded
  if (googleMapsInitPromise) {
    return googleMapsInitPromise;
  }

  googleMapsInitPromise = new Promise<void>((resolve, reject) => {
    // Already fully loaded
    if (window.google?.maps?.places?.AutocompleteService) {
      resolve();
      return;
    }

    // Check if script already exists in DOM
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    
    if (existingScript) {
      // Script exists, wait for places API to become available
      waitForPlacesAPI()
        .then(resolve)
        .catch(reject);
      return;
    }

    if (!GOOGLE_MAPS_API_KEY) {
      googleMapsInitPromise = null; // Reset so we can retry if env var gets set
      reject(new Error('Missing VITE_GOOGLE_MAPS_API_KEY'));
      return;
    }

    // Create and load script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      // Wait for places to be fully initialized
      waitForPlacesAPI()
        .then(resolve)
        .catch(reject);
    };

    script.onerror = () => {
      googleMapsInitPromise = null; // Reset so we can retry
      reject(new Error('Failed to load Google Maps script'));
    };

    document.head.appendChild(script);
  });

  return googleMapsInitPromise;
};

interface AddressData {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
}

interface AddressInputProps {
  value: AddressData;
  onChange: (address: AddressData) => void;
  errors?: {
    addressLine1?: { message?: string };
    addressLine2?: { message?: string };
    city?: { message?: string };
    state?: { message?: string };
    postalCode?: { message?: string };
  };
  states?: { code: string; name?: string }[];
  disabled?: boolean;
  /** When true, drop the "required" CSS class on labels so the asterisks disappear. */
  hideRequiredMarkers?: boolean;
}

interface Prediction {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

const parseAddressComponents = (addressComponents: google.maps.GeocoderAddressComponent[]) => {
  const components: Record<string, string> = {};

  addressComponents.forEach(component => {
    const componentType = component.types[0];
    components[componentType] = component.long_name;
    // Also store short_name for state
    if (componentType === 'administrative_area_level_1') {
      components['state_short'] = component.short_name;
    }
  });

  const streetNumber = components.street_number || '';
  const route = components.route || '';
  const addressLine1 = `${streetNumber} ${route}`.trim();
  const city = components.locality || components.postal_town || components.sublocality_level_1 || '';
  
  // Try to get state code - first try matching against usStates, then use short_name
  let state = '';
  const stateLongName = components.administrative_area_level_1;
  const stateShortName = components.state_short;
  
  // Try to find state code from our list
  const matchedState = usStates.find(
    s => s.label === stateLongName || s.value === stateShortName
  );
  state = matchedState?.value || stateShortName || '';
  
  const postalCode = components.postal_code || '';

  return { addressLine1, city, state, postalCode };
};

const AddressInput: React.FC<AddressInputProps> = ({
  value,
  onChange,
  errors,
  states = [],
  disabled = false,
  hideRequiredMarkers = false,
}) => {
  const labelClassName = hideRequiredMarkers ? '' : 'required';
  console.log('AddressInput RENDER - value:', value);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  
  const [isApiReady, setIsApiReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState(value.addressLine1 || '');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  // Debounce timer ref
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Use provided states or fall back to US states
  const stateOptions = states.length > 0 ? states : usStates.map(s => ({ code: s.value, name: s.label }));

  // Sync input value when value prop changes (for form reset, etc.)
  useEffect(() => {
    if (value.addressLine1 !== inputValue && !showDropdown) {
      setInputValue(value.addressLine1 || '');
    }
  }, [value.addressLine1]);

  // Initialize Google Places services
  useEffect(() => {
    let isMounted = true;

    const initServices = async () => {
      try {
        await loadGoogleMapsScript();
        
        if (!isMounted) return;
        
        if (!window.google?.maps?.places?.AutocompleteService) {
          console.error('AddressInput: Google Maps Places API not available after loading');
          return;
        }

        // Create a dummy element for PlacesService (it requires an element or map)
        const dummyElement = document.createElement('div');
        
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        placesServiceRef.current = new window.google.maps.places.PlacesService(dummyElement);
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        
        console.log('AddressInput: Google Maps Places API initialized successfully');
        setIsApiReady(true);
      } catch (error) {
        console.error('AddressInput: Failed to initialize Google Maps:', error);
      }
    };

    initServices();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch predictions when input changes
  const fetchPredictions = useCallback((input: string) => {
    console.log('fetchPredictions called with:', input);
    console.log('autocompleteServiceRef.current:', !!autocompleteServiceRef.current);
    console.log('isApiReady:', isApiReady);
    
    if (!input.trim()) {
      console.log('Input is empty, clearing predictions');
      setPredictions([]);
      setShowDropdown(false);
      return;
    }
    
    if (!autocompleteServiceRef.current) {
      console.warn('AutocompleteService not available yet');
      return;
    }

    console.log('Making Places API request...');
    setIsLoading(true);

    autocompleteServiceRef.current.getPlacePredictions(
      {
        input,
        componentRestrictions: { country: 'us' },
        types: ['address'],
        sessionToken: sessionTokenRef.current || undefined,
      },
      (results, status) => {
        console.log('Places API response - status:', status, 'results:', results?.length || 0);
        setIsLoading(false);

        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          const formattedPredictions: Prediction[] = results.map(result => ({
            placeId: result.place_id,
            description: result.description,
            mainText: result.structured_formatting.main_text,
            secondaryText: result.structured_formatting.secondary_text,
          }));
          console.log('Setting predictions:', formattedPredictions.length);
          setPredictions(formattedPredictions);
          setShowDropdown(true);
          setHighlightedIndex(-1);
        } else {
          console.log('No results or error, clearing predictions');
          setPredictions([]);
          setShowDropdown(false);
        }
      }
    );
  }, [isApiReady]);

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    console.log('handleInputChange - newValue:', newValue);
    setInputValue(newValue);

    // Also update the form value for addressLine1
    onChange({
      ...value,
      addressLine1: newValue,
    });

    // Debounce the API call
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    console.log('Setting up debounce timer for:', newValue);
    debounceTimerRef.current = setTimeout(() => {
      console.log('Debounce timer fired for:', newValue);
      fetchPredictions(newValue);
    }, 300);
  };

  // Handle prediction selection
  const handleSelectPrediction = useCallback((prediction: Prediction) => {
    if (!placesServiceRef.current) return;

    setShowDropdown(false);
    setIsLoading(true);

    placesServiceRef.current.getDetails(
      {
        placeId: prediction.placeId,
        fields: ['address_components', 'formatted_address'],
        sessionToken: sessionTokenRef.current || undefined,
      },
      (place, status) => {
        setIsLoading(false);

        // Reset session token after use
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();

        if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.address_components) {
          const parsed = parseAddressComponents(place.address_components);
          
          setInputValue(parsed.addressLine1);
          
          onChange({
            addressLine1: parsed.addressLine1,
            addressLine2: value.addressLine2, // Keep existing
            city: parsed.city,
            state: parsed.state,
            postalCode: parsed.postalCode,
          });
        }
      }
    );
  }, [onChange, value.addressLine2]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || predictions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < predictions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : predictions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < predictions.length) {
          handleSelectPrediction(predictions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        inputRef.current && 
        !inputRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFieldChange = (field: keyof AddressData, fieldValue: string) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  return (
    <div className="space-y-4">
      {/* Address Line 1 with integrated autocomplete */}
      <div className="space-y-1.5">
        <Label className={labelClassName}>Address Line 1</Label>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <Input
            ref={inputRef}
            autoComplete="off"
            placeholder={isApiReady ? "Start typing your address..." : "Loading address search..."}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (predictions.length > 0) setShowDropdown(true);
            }}
            disabled={disabled || !isApiReady}
            className="pl-9"
          />
          {isLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
            </div>
          )}
          
          {/* Custom Predictions Dropdown */}
          {showDropdown && predictions.length > 0 && (
            <div 
              ref={dropdownRef}
              className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-[99999] max-h-60 overflow-y-auto"
              onMouseDown={(e) => e.preventDefault()} // Prevent blur on input
            >
              {predictions.map((prediction, index) => (
                <button
                  key={prediction.placeId}
                  type="button"
                  className={cn(
                    "w-full px-3 py-2 text-left hover:bg-slate-100 flex items-start gap-2 transition-colors",
                    highlightedIndex === index && "bg-slate-100"
                  )}
                  onClick={() => handleSelectPrediction(prediction)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <MapPin className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {prediction.mainText}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {prediction.secondaryText}
                    </div>
                  </div>
                </button>
              ))}
              <div className="px-3 py-1.5 border-t border-slate-100 flex justify-end">
                <img 
                  src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png" 
                  alt="Powered by Google" 
                  className="h-3"
                />
              </div>
            </div>
          )}
        </div>
        {errors?.addressLine1?.message && (
          <p className="text-[0.8rem] text-red-500">{errors.addressLine1.message}</p>
        )}
        <p className="text-xs text-slate-500">
          Start typing and select from suggestions to auto-fill city, state, and postal code
        </p>
      </div>

      {/* Address Line 2 - Optional */}
      <div className="space-y-1.5">
        <Label>Apt, Suite, Unit (Optional)</Label>
        <Input
          placeholder="Apt 4B, Suite 100, etc."
          value={value.addressLine2 || ''}
          onChange={(e) => handleFieldChange('addressLine2', e.target.value.trimStart())}
          disabled={disabled}
        />
        {errors?.addressLine2?.message && (
          <p className="text-[0.8rem] text-red-500">{errors.addressLine2.message}</p>
        )}
      </div>

      {/* City, State, Postal Code in row */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="space-y-1.5">
          <Label className={labelClassName}>City</Label>
          <Input
            placeholder="City"
            value={value.city || ''}
            onChange={(e) => handleFieldChange('city', e.target.value.trimStart())}
            disabled={disabled}
          />
          {errors?.city?.message && (
            <p className="text-[0.8rem] text-red-500">{errors.city.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className={labelClassName}>State</Label>
          <Select
            value={value.state || ''}
            onValueChange={(val) => handleFieldChange('state', val)}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              {stateOptions.map((state) => (
                <SelectItem key={state.code} value={state.code}>
                  {state.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors?.state?.message && (
            <p className="text-[0.8rem] text-red-500">{errors.state.message}</p>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className={labelClassName}>Postal Code</Label>
          <Input
            maxLength={5}
            placeholder="12345"
            value={value.postalCode || ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                handleFieldChange('postalCode', '');
                return;
              }
              // Only allow digits, up to 5 characters
              if (/^\d{0,5}$/.test(raw)) {
                handleFieldChange('postalCode', raw);
              }
            }}
            disabled={disabled}
          />
          {errors?.postalCode?.message && (
            <p className="text-[0.8rem] text-red-500">{errors.postalCode.message}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddressInput;
export { AddressInput };
export type { AddressData, AddressInputProps };

